// src/utils/scheduleModel.js
//
// Scheduling pilot (Pass 43) — vocabulary + derivations that model Sam's SAS
// 2026/27 operating workbook on top of the canonical `bookings` schedule.
//
// Pure, dependency-free helpers so they are trivially unit-testable and reusable
// by the ops UI, the instructor UI, the seed, and the import adapter.
import { zonedMinutesOfDay, zonedDateKey, zonedDateParts, resolveBookingTimeZone } from './opsTime';

// ── Schedule entry type (item 5) ─────────────────────────────────────────────
// Internal events are BOOKINGS with a non-client entry type — one source of
// truth, no fake client fields, participates in every view/conflict/audit.
export const SCHEDULE_ENTRY_TYPES = {
  client_booking: { label: 'Lesson', color: '#2563EB', isClient: true },
  training: { label: 'Training', color: '#9333EA', isClient: false },
  meeting: { label: 'Meeting', color: '#0891B2', isClient: false },
  admin: { label: 'Admin', color: '#6B7280', isClient: false },
  travel: { label: 'Travel', color: '#D97706', isClient: false },
  blocked: { label: 'Unavailable', color: '#DC2626', isClient: false },
  other: { label: 'Other', color: '#57564F', isClient: false },
};
export const isClientBooking = (b) => (SCHEDULE_ENTRY_TYPES[b?.scheduleEntryType || 'client_booking']?.isClient) !== false;
export const normalizeEntryType = (v) => (v && SCHEDULE_ENTRY_TYPES[v] ? v : 'client_booking');

// Pass 58F2 — a RETIRED/SUPERSEDED booking is a seed/bootstrap or rebaselined row that no longer
// represents live operational work (e.g. the 26/27 bootstrap placeholders whose Motus IDs were
// detached when the live sheet repopulated). It is marked canonically with `active === false` and/or
// `lifecycleState === 'retired_superseded'` — the SAME signal scheduleVisibility already respects.
// Operational counts (unassigned, today/tomorrow, attention) must exclude these so they never inflate
// the live schedule with stale placeholders.
export const isRetiredSuperseded = (b = {}) => b?.active === false || b?.lifecycleState === 'retired_superseded' || b?.rebaselineSuperseded === true;
// A LIVE operational booking: not cancelled and not retired/superseded.
export const isLiveBooking = (b = {}) => b?.status !== 'canceled' && b?.status !== 'cancelled' && !isRetiredSuperseded(b);

// ── Commercial / payment state (item 4) — SEPARATE from scheduling `status` ───
export const PAYMENT_STATES = {
  reservation_inquiry: { label: 'Reservation / Inquiry', short: 'Reservation', color: '#8A8980', bg: 'rgba(138,137,128,0.12)' },
  confirmed_not_paid: { label: 'Confirmed — Not Paid', short: 'Not paid', color: '#DC2626', bg: 'rgba(220,38,38,0.10)' },
  deposit_paid: { label: 'Confirmed — Deposit Paid', short: 'Deposit', color: '#D97706', bg: 'rgba(217,119,6,0.12)' },
  fully_paid: { label: 'Confirmed — Fully Paid', short: 'Paid', color: '#16A34A', bg: 'rgba(22,163,74,0.12)' },
};
// Map Sam's workbook "Status" strings → canonical paymentState.
export function mapSamPaymentState(raw = '') {
  const s = String(raw).toLowerCase().replace(/[^a-z]/g, '');
  if (s.includes('fullypaid')) return 'fully_paid';
  if (s.includes('depositpaid') || s.includes('deposit')) return 'deposit_paid';
  if (s.includes('notpaid') || s.includes('confirmednotpaid')) return 'confirmed_not_paid';
  if (s.includes('reservation') || s.includes('inquiry') || s.includes('enquiry')) return 'reservation_inquiry';
  return '';
}
export const normalizePaymentState = (v) => (v && PAYMENT_STATES[v] ? v : '');

// ── Cancellation financial resolution (item 12) — separate from status ────────
export const CANCELLATION_RESOLUTIONS = {
  no_refund: { label: 'Cancelled — No Refund', color: '#DC2626' },
  credit_next_season: { label: 'Credit Owed Next Season', color: '#D97706' },
  refund: { label: 'Refunded', color: '#2563EB' },
};
export const normalizeCancellationResolution = (v) => (v && CANCELLATION_RESOLUTIONS[v] ? v : '');

// ── Discipline (item 6) ──────────────────────────────────────────────────────
export const DISCIPLINES = {
  ski: { label: 'Ski', needs: ['ski'] },
  snowboard: { label: 'Snowboard', needs: ['snowboard'] },
  mix_ski: { label: 'Mix (Ski majority)', needs: ['ski', 'snowboard'] },
  mix_sb: { label: 'Mix (SB majority)', needs: ['ski', 'snowboard'] },
};
export function mapSamDiscipline(raw = '') {
  const s = String(raw).toLowerCase();
  if (s.includes('ski majority')) return 'mix_ski';
  if (s.includes('sb majority') || s.includes('snowboard majority')) return 'mix_sb';
  if (s.includes('snowboard') || s === 'sb') return 'snowboard';
  if (s.includes('mix')) return 'mix_ski';
  if (s.includes('ski')) return 'ski';
  return '';
}

// ── Skill level (item 6/8) ───────────────────────────────────────────────────
export const SKILL_LEVELS = ['first_timer', 'novice', 'beginner', 'intermediate', 'advanced'];
export const SKILL_LABELS = { first_timer: 'First Timer', novice: 'Novice', beginner: 'Beginner', intermediate: 'Intermediate', advanced: 'Advanced' };
export function mapSamSkillLevel(raw = '') {
  const s = String(raw).toLowerCase().replace(/[^a-z]/g, '');
  if (s.includes('firsttimer')) return 'first_timer';
  if (s.includes('novice')) return 'novice';
  if (s.includes('beginner')) return 'beginner';
  if (s.includes('intermediate')) return 'intermediate';
  if (s.includes('advanced')) return 'advanced';
  return '';
}

// ── Instructor category (item 7) ─────────────────────────────────────────────
export const INSTRUCTOR_CATEGORIES = {
  sas_school: { label: 'SAS Snowsports School' },
  sas_collective: { label: 'SAS Collective' },
};
export const normalizeInstructorCategory = (v) => (v === 'sas_collective' ? 'sas_collective' : v === 'sas_school' ? 'sas_school' : '');

// ── Booking type → duration category (item 3 slot derivation) ─────────────────
export function deriveDurationCategory(bookingType = '') {
  const s = String(bookingType).toLowerCase();
  // Night session (e.g. "Premium - Night", "MOS - Night") — a distinct 2h evening product with fixed
  // starts (16:30 / 17:00); the exact end derives from the source start (+2h) via DURATION_HOURS.night.
  if (s.includes('night')) return 'night';
  if (s.includes('half day') || s.includes('half-day')) return 'half_day';
  if (s.includes('full day') || s.includes('full-day')) return 'full_day';
  return '';
}

// ── Canonical SAS lesson durations (single source of truth) ───────────────────
// The ONE place full-day/half-day hours are defined. Both booking cases and the
// Booking Data Master sheet sync derive endAt from here so a Full Day means the
// same span everywhere. Unknown type falls back to a 2h nominal block (matches
// the historical deriveSlots synthesis) — enough to occupy a slot, never silently
// zero-length.
export const DURATION_HOURS = { full_day: 6, half_day: 3, night: 2 };
export const DEFAULT_DURATION_HOURS = 2;

// startAt (+ durationCategory or bookingType) → canonical endAt (Date | null).
export function deriveEndAt(startAt, { durationCategory = '', bookingType = '' } = {}) {
  const start = toDate(startAt);
  if (!start) return null;
  const cat = durationCategory || deriveDurationCategory(bookingType);
  const hrs = DURATION_HOURS[cat] || DEFAULT_DURATION_HOURS;
  return new Date(start.getTime() + hrs * 3600 * 1000);
}

// ── AM / PM / Night slot derivation (item 3) ─────────────────────────────────
// Slots are DERIVED from exact startAt/endAt — the precise times remain canonical.
// A full-day lesson overlaps AM and PM; night is a distinct capacity period.
export const SLOT_WINDOWS = {
  am: { startHour: 6, endHour: 12, label: 'AM' },
  pm: { startHour: 12, endHour: 17, label: 'PM' },
  night: { startHour: 17, endHour: 23, label: 'Night' },
};
function toDate(v) {
  if (!v) return null;
  if (typeof v?.toDate === 'function') return v.toDate();
  if (v?.seconds) return new Date(v.seconds * 1000);
  if (v instanceof Date) return v;
  const d = new Date(v); return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Which of AM/PM/Night a booking occupies, derived from its start/end times.
 * If endAt is missing, synthesise from durationCategory (full=6h, half=3h) or 2h.
 * Returns { am, pm, night } booleans.
 */
export function deriveSlots(booking = {}, { timeZone = '' } = {}) {
  const start = toDate(booking.startAt);
  if (!start) return { am: false, pm: false, night: false };
  let end = toDate(booking.endAt);
  if (!end || end <= start) {
    // Same canonical derivation used by the sheet sync (deriveEndAt).
    end = deriveEndAt(start, { durationCategory: booking.durationCategory, bookingType: booking.bookingType });
  }
  // Pass 82C.2 §15: minutes-since-midnight in the BOOKING's timezone (stored zone wins → org-aware caller's zone →
  // legacy SAS JST), NOT the viewer's timezone. So 09:00 is AM whether the booking is Tokyo-local (00:00Z) or
  // Denver-local (16:00Z) — different absolute instants, same operational AM slot. SAS bookings carry 'Asia/Tokyo'
  // so this is byte-identical to the former JST-hardcoded behaviour.
  const tz = resolveBookingTimeZone(booking, { timeZone });
  const s = zonedMinutesOfDay(start, tz);
  // clamp end to same zoned day for slot purposes (cap at 23:00)
  const e = zonedDateKey(end, tz) === zonedDateKey(start, tz) ? zonedMinutesOfDay(end, tz) : 23 * 60;
  const overlaps = (win) => s < win.endHour * 60 && e > win.startHour * 60;
  return {
    am: overlaps(SLOT_WINDOWS.am),
    pm: overlaps(SLOT_WINDOWS.pm),
    night: overlaps(SLOT_WINDOWS.night),
  };
}

// ── Additional guests parsing (item 8) ───────────────────────────────────────
// Sam's format: "Harold, 42. Kumar, 20." — parse into [{name, dob}] WITHOUT
// losing the raw string. `dob` here is age-or-dob text as written (Sam mixes ages
// and DOBs); we preserve it verbatim rather than inventing a date.
export function parseAdditionalGuests(raw = '') {
  const text = String(raw || '').trim();
  if (!text) return [];
  return text.split('.').map((seg) => seg.trim()).filter(Boolean).map((seg) => {
    const [name, ...rest] = seg.split(',');
    return { name: (name || '').trim(), dob: rest.join(',').trim() };
  }).filter((g) => g.name);
}
export const formatAdditionalGuests = (arr = []) => (arr || []).map((g) => (g.dob ? `${g.name}, ${g.dob}` : g.name)).join('. ');

// ── Season identity (Pass 46) ────────────────────────────────────────────────
// A ski season spans Dec→Apr. Explicit booking.seasonId wins; otherwise derive
// from startAt (Nov–Dec → that year's season; Jan–Jun → previous year's season).
export const SEASONS = ['2024_25', '2025_26', '2026_27'];
export const seasonLabel = (id) => (id ? id.replace('_', '/') : '');
export function deriveSeasonId(booking = {}, { timeZone = '' } = {}) {
  if (booking.seasonId) return booking.seasonId; // explicit seasonId is always authoritative
  const v = booking.startAt;
  const d = !v ? null : typeof v?.toDate === 'function' ? v.toDate() : v?.seconds ? new Date(v.seconds * 1000) : v instanceof Date ? v : new Date(v);
  if (!d || Number.isNaN(d.getTime())) return '';
  // Pass 82C.2 §16 — when we must infer the season from an instant, read the year/month in the booking/org timezone
  // (booking zone wins → explicit option → legacy SAS JST), NEVER the browser-local calendar. A Jul-boundary instant
  // therefore lands in the same season for every operator, and non-JST tenants infer in their own zone.
  const tz = resolveBookingTimeZone(booking, { timeZone });
  const parts = zonedDateParts(d, tz);
  if (!parts) return '';
  const y = parts.year; const m = parts.month - 1; // 0=Jan
  const startYear = m >= 6 ? y : y - 1; // Jul+ starts a new season; Jan–Jun belongs to prior year's season
  return `${startYear}_${String((startYear + 1) % 100).padStart(2, '0')}`;
}
export const isHistoricalSeason = (id, currentId = '2026_27') => Boolean(id) && id < currentId;

// ── Operational readiness (Pass 45 item 9) — READY vs NEEDS ATTENTION ─────────
// A small, explicit list of fields required to actually run a client lesson.
// Internal events have relaxed requirements (no client/guest needed).
export function assessOperationalReadiness(booking = {}) {
  const missing = [];
  const client = isClientBooking(booking);
  if (booking.status === 'canceled') return { ready: true, missing: [] }; // cancelled ≠ not-ready
  if (!booking.instructorId) missing.push('instructor');
  if (!booking.startAt) missing.push('start time');
  if (!booking.locationLabel) missing.push('meeting point');
  if (client) {
    if (!booking.discipline) missing.push('discipline');
    if (!(booking.requesterName || booking.leadGuest?.fullName)) missing.push('guest');
  }
  if (booking._ambiguousInstructor) missing.push('ambiguous instructor');
  return { ready: missing.length === 0, missing };
}

// ── Instructor discipline compatibility (item 6) — WARN, never hard-block ─────
/**
 * @param capability { skiLevel, snowboardLevel, primaryDiscipline }
 * @param discipline canonical discipline key
 * @returns { compatible, warning }
 */
export function checkDisciplineCompatibility(capability = {}, discipline = '') {
  const spec = DISCIPLINES[discipline];
  if (!spec) return { compatible: true, warning: '' };
  const has = {
    ski: capability.skiLevel != null && capability.skiLevel !== '',
    snowboard: capability.snowboardLevel != null && capability.snowboardLevel !== '',
  };
  const missing = spec.needs.filter((d) => !has[d]);
  if (missing.length === 0) return { compatible: true, warning: '' };
  const disc = spec.label;
  const miss = missing.map((d) => (d === 'ski' ? 'ski' : 'snowboard')).join(' & ');
  return { compatible: false, warning: `This instructor has no recorded ${miss} qualification for a ${disc} lesson.` };
}
